import { Component, OnInit, Input, Output, EventEmitter, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { StringHelper } from '../../../../shared/utils/string-helper';
import { ScheduleItem } from '../room-schedule-item/room-schedule-item.component';
import { ICreateReservationParams } from '../create-reservation-dialog/create-reservation-dialog-base.component';
import { TranslateService } from '@ngx-translate/core';
import { ResourceType } from 'src/app/shared/models/resource-type';

@Component({
  selector: 'app-collapsible-schedule',
  templateUrl: './collapsible-schedule.component.html',
  styleUrls: ['./collapsible-schedule.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('collapsed', [
      state('true', style({
        height: '0'
      })),
      state('false', style({
        height: '*'
      })),
      transition('* => false', [
        style({ height: 0 }),
        animate(300, style({ height: '*' }))
      ]),
      transition('* => true', [
        style({ height: '*' }),
        animate(300, style({ height: 0 }))
      ]
      )
    ])
  ]
})
export class CollapsibleScheduleComponent implements OnInit {

  /**
   * Events to display
   *
   * @type {CollapsibleScheduleItem[]}
   * @memberof CollapsibleScheduleComponent
   */
  @Input() scheduleItems: ScheduleItem[];

  /**
   * Start of timeline
   *
   * @type {number}
   * @memberof CollapsibleScheduleComponent
   */
  @Input() startHour: number;

  /**
   * End of timeline
   *
   * @type {number}
   * @memberof CollapsibleScheduleComponent
   */
  @Input() endHour: number;

  /**
   * Collapse state
   *
   * @type {boolean}
   * @memberof CollapsibleScheduleComponent
   */
  @Input() collapsed: boolean;

  /**
   * Type of items to display (room or device)
   *
   * @type {string}
   * @memberof CollapsibleScheduleComponent
   */
  @Input() type: ResourceType;

  /**
   * Event raised on click on "create new"
   *
   * @memberof RoomScheduleItemComponent
   */
  @Output() createReservation = new EventEmitter<ICreateReservationParams>();

  renderContent: boolean;

  table: ICollapsibleScheduleTable[] = [];

  constructor(private cdr: ChangeDetectorRef, private translate: TranslateService) { }

  ngOnInit() {

    if (this.type === undefined) {
      throw new Error('Please provide property ' + nameof<CollapsibleScheduleComponent>('type'));
    }

    // init
    this.renderContent = !this.collapsed;

    // create table
    for (let i = this.startHour; i <= this.endHour; i++) {
      this.table.push({ hour: i, items: [] });
    }
    // fill items into table
    this.scheduleItems.forEach(i => {
        let index = i.from.getHours() - this.startHour;

        if (index < 0) {
          // test if also would end before table range
          const endsBeforeStartOfRange = (i.to.getHours() - this.startHour) <= 0;
          if (endsBeforeStartOfRange) {
            return; // skip
          }
          index = 0; // adjust to first index
        }

        if (index >= this.table.length) {
          return; // out of range
        }

        this.table[index].items.push(i);
      });

    this.cdr.markForCheck();
  }

  /**
   * Format time span
   *
   * @param {CollapsibleScheduleItem} item
   * @returns
   * @memberof CollapsibleScheduleComponent
   */
  getTimeSpanString(item: ScheduleItem) {
    return StringHelper.formatTimeSpan(item.from.getHours(), item.from.getMinutes(), item.to.getHours(), item.to.getMinutes());
  }

  /**
   * Format hour (e.g. 22 => 22:00)
   *
   * @param {number} hour
   * @returns
   * @memberof CollapsibleScheduleComponent
   */
  getTimeString(hour: number) {
    return StringHelper.formatTime(hour);
  }

  /**
    * Handle click on "create reservation"
    *
    * @memberof CollapsibleScheduleComponent
    */
  onCreateReservation(params: ICreateReservationParams) {
    this.createReservation.emit(params);
  }

  /**
   * Before animation
   *
   * @memberof CollapsibleScheduleComponent
   */
  onCollapsedStarted() {
    if (!this.collapsed) { // about to open
      this.renderContent = true;
    }
  }

  /**
   * After animation
   *
   * @memberof CollapsibleScheduleComponent
   */
  onCollapsedDone() {
    if (this.collapsed) { // has closed
      this.renderContent = false;
    }
  }

}

export class ScheduleBlock {

  /**
   * Type of block
   *
   * @type {ScheduleBlockType}
   * @memberof ScheduleBlock
   */
  type: ScheduleBlockType;

  /**
   * Start of event
   *
   * @type {Date}
   * @memberof CollapsibleScheduleItem
   */
  from: Date;

  /**
   * End of event
   *
   * @type {Date}
   * @memberof CollapsibleScheduleItem
   */
  to: Date;

  /**
   * display text
   *
   * @type {string}
   * @memberof CollapsibleScheduleItem
   */
  text: string;

  /**
   * CSS color code
   *
   * @type {string}
   * @memberof ScheduleBlock
   */
  color: string;
}

export enum ScheduleBlockType {

  /**
   * eg. Ressource not availablet due to calendar limitation
   */
  Blocker,

  /**
   * The resource is occupied
   */
  Occupancy,

  /**
   * The resource is occupied by the current user
   */
  MyOccupancy,

  /**
   * The resource can be booked
   */
  Available
}

/**
 * Internal table data
 *
 * @interface ICollapsibleScheduleTable
 */
interface ICollapsibleScheduleTable {
  hour: number;
  items: ScheduleItem[];
}
